import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

/**
 * Email OTP Protected Route
 * - Login with email only (whitelist enforced)
 * - Request verification code via email
 * - Verify code, set httpOnly cookie, then render children
 * Backend endpoints (proxy to /api):
 *   POST   /api/auth/request-code  { email }
 *   POST   /api/auth/verify-code   { email, code }
 *   GET    /api/auth/me
 *   POST   /api/auth/logout
 */
const EmailOtpProtectedRoute = ({ children, allowedLevels, allowedEmails }) => {
  const { t } = useTranslation();
  const API_BASE = useMemo(() => process.env.REACT_APP_API_BASE || "/api", []);
  const [checking, setChecking] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);

  // form states
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // resend countdown
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  // whitelist status
  const [isWhitelisted, setIsWhitelisted] = useState(null);
  const [whitelistLevel, setWhitelistLevel] = useState(null);
  const wlTimerRef = useRef(null);

  const validEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s || "").trim());
  const normalizeAuthMessage = useCallback(
    (authResult, fallback = "") => {
      const code =
        typeof authResult === "object" && authResult !== null
          ? String(authResult.code || "").trim()
          : "";
      const text =
        typeof authResult === "object" && authResult !== null
          ? String(authResult.message || "").trim()
          : String(authResult || "").trim();
      const codeMessages = {
        INVALID_EMAIL: t("EmailOtp.error.invalidEmail"),
        NOT_WHITELISTED: t("EmailOtp.error.notWhitelisted"),
        BLACKLISTED_EMAIL: t("EmailOtp.error.blacklisted"),
        RATE_LIMITED: t("EmailOtp.error.rateLimited"),
        OTP_DEV_PRINTED: t("EmailOtp.message.devPrinted"),
        OTP_SENT: t("EmailOtp.message.codeSent"),
        EMAIL_SEND_FAILED: t("EmailOtp.error.sendFailed"),
        SERVER_ERROR: t("EmailOtp.error.serverError"),
        CODE_REQUIRED: t("EmailOtp.error.codeRequired"),
        OTP_INVALID_OR_EXPIRED: t("EmailOtp.error.invalidOrExpired"),
        OTP_EXPIRED: t("EmailOtp.error.expired"),
        OTP_INCORRECT: t("EmailOtp.error.incorrect"),
      };
      const legacyMessages = {
        "Invalid email format": t("EmailOtp.error.invalidEmail"),
        "Only whitelisted emails can sign in. Please contact the administrator.":
          t("EmailOtp.error.notWhitelisted"),
        "This email is blocked from signing in": t("EmailOtp.error.blacklisted"),
        "Too many requests. Please try again later.":
          t("EmailOtp.error.rateLimited"),
        "Development mode: the verification code was printed to the server console":
          t("EmailOtp.message.devPrinted"),
        "Verification code sent, please check your inbox": t(
          "EmailOtp.message.codeSent"
        ),
        "Failed to send email. Please try again later.":
          t("EmailOtp.error.sendFailed"),
        "Server error": t("EmailOtp.error.serverError"),
        "Please enter the verification code": t("EmailOtp.error.codeRequired"),
        "The verification code is invalid or expired": t(
          "EmailOtp.error.invalidOrExpired"
        ),
        "The verification code has expired": t("EmailOtp.error.expired"),
        "The verification code is incorrect": t("EmailOtp.error.incorrect"),
      };
      return codeMessages[code] || legacyMessages[text] || text || fallback;
    },
    [t]
  );
  const normalizedAllowedLevels = useMemo(() => {
    if (!Array.isArray(allowedLevels) || allowedLevels.length === 0) return null;
    return allowedLevels.map((l) => String(l || "").trim().toLowerCase());
  }, [allowedLevels]);
  const normalizedAllowedEmails = useMemo(() => {
    if (!Array.isArray(allowedEmails) || allowedEmails.length === 0) return null;
    return allowedEmails
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
  }, [allowedEmails]);
  const isAllowedEmail = useCallback(
    (value) => {
      if (!normalizedAllowedEmails) return true;
      return normalizedAllowedEmails.includes(
        String(value || "").trim().toLowerCase(),
      );
    },
    [normalizedAllowedEmails],
  );
  const isAllowedLevel = useCallback(
    (level) => {
      if (!normalizedAllowedLevels) return true;
      const list = Array.isArray(level)
        ? level
        : [String(level || "").trim().toLowerCase()].filter(Boolean);
      if (list.includes("superadmin")) return true;
      return list.some((v) =>
        normalizedAllowedLevels.includes(String(v || "").trim().toLowerCase())
      );
    },
    [normalizedAllowedLevels]
  );
  const isEligible =
    isWhitelisted === true && isAllowedLevel(whitelistLevel) && isAllowedEmail(email);
  const canSend =
    !sending && validEmail(email) && countdown === 0 && isEligible;
  const canVerify =
    !verifying && validEmail(email) && String(code).trim().length >= 4;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/me`, {
          credentials: "include",
        });
        const j = await resp.json().catch(() => ({}));
        if (mounted && j?.ok) {
          if (!isAllowedEmail(j?.profile?.email)) {
            setIsAuthed(false);
            setError(t("EmailOtp.error.notWhitelisted"));
            return;
          }
          if (isAllowedLevel(null)) {
            setIsAuthed(true);
          } else {
            try {
              const wlResp = await fetch(`${API_BASE}/auth/check-whitelist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: String(j?.profile?.email || "") }),
              });
              const wlJson = await wlResp.json().catch(() => ({}));
              const wlLevel = wlJson?.level || null;
              setWhitelistLevel(wlLevel);
              if (wlJson?.whitelisted && isAllowedLevel(wlLevel)) {
                setIsAuthed(true);
              } else {
                setIsAuthed(false);
                setIsWhitelisted(Boolean(wlJson?.whitelisted));
                setError(t("EmailOtp.error.notWhitelisted"));
              }
            } catch (_) {
              setIsAuthed(false);
            }
          }
        }
      } catch (_) {
      } finally {
        if (mounted) setChecking(false);
      }
    })();
    return () => {
      mounted = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (wlTimerRef.current) {
        clearTimeout(wlTimerRef.current);
        wlTimerRef.current = null;
      }
    };
  }, [API_BASE, isAllowedEmail, isAllowedLevel, t]);

  // check whitelist when email changes (debounced)
  useEffect(() => {
    if (!validEmail(email)) {
      setIsWhitelisted(null);
      setWhitelistLevel(null);
      if (wlTimerRef.current) {
        clearTimeout(wlTimerRef.current);
        wlTimerRef.current = null;
      }
      return;
    }
    if (wlTimerRef.current) {
      clearTimeout(wlTimerRef.current);
      wlTimerRef.current = null;
    }
    wlTimerRef.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/check-whitelist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: String(email).trim() }),
        });
        const j = await resp.json().catch(() => ({}));
        setIsWhitelisted(Boolean(j?.whitelisted));
        setWhitelistLevel(j?.level || null);
      } catch (_) {
        setIsWhitelisted(false);
        setWhitelistLevel(null);
      }
    }, 300);
    return () => {
      if (wlTimerRef.current) {
        clearTimeout(wlTimerRef.current);
        wlTimerRef.current = null;
      }
    };
  }, [email, API_BASE]);

  const startCountdown = (sec = 60) => {
    setCountdown(sec);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((v) => {
        if (v <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return v - 1;
      });
    }, 1000);
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!validEmail(email)) {
      setError(t("EmailOtp.error.invalidEmail"));
      return;
    }
    if (!isAllowedEmail(email)) {
      setError(t("EmailOtp.error.notWhitelisted"));
      return;
    }
    // 仅白名单邮箱可请求验证码
    let wl = isWhitelisted;
    let wlLevel = whitelistLevel;
    if (wl === null) {
      try {
        const resp = await fetch(`${API_BASE}/auth/check-whitelist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: String(email).trim() }),
        });
        const j = await resp.json().catch(() => ({}));
        wl = Boolean(j?.whitelisted);
        setIsWhitelisted(wl);
        wlLevel = j?.level || null;
        setWhitelistLevel(wlLevel);
        if (wl && !isAllowedLevel(wlLevel)) {
          setError(t("EmailOtp.error.notWhitelisted"));
          return;
        }
      } catch (_) {
        wl = false;
      }
    }
    if (!wl || !isAllowedLevel(wlLevel)) {
      setError(t("EmailOtp.error.notWhitelisted"));
      return;
    }
    try {
      setSending(true);
      const payload = {
        email: String(email).trim(),
      };
      const resp = await fetch(`${API_BASE}/auth/request-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j?.ok) {
        setError(normalizeAuthMessage(j, t("EmailOtp.error.sendFailed")));
        return;
      }
      setCodeSent(true);
      setMessage(normalizeAuthMessage(j, t("EmailOtp.message.codeSent")));
      startCountdown(60);
    } catch (err) {
      setError(normalizeAuthMessage(err?.message, t("EmailOtp.error.sendFailed")));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!validEmail(email)) {
      setError(t("EmailOtp.error.invalidEmail"));
      return;
    }
    if (!isAllowedEmail(email)) {
      setError(t("EmailOtp.error.notWhitelisted"));
      return;
    }
    if (!String(code).trim()) {
      setError(t("EmailOtp.error.codeRequired"));
      return;
    }
    if (!isAllowedLevel(whitelistLevel)) {
      setError(t("EmailOtp.error.notWhitelisted"));
      return;
    }
    try {
      setVerifying(true);
      const resp = await fetch(`${API_BASE}/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // receive httpOnly cookie
        body: JSON.stringify({
          email: String(email).trim(),
          code: String(code).trim(),
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j?.ok) {
        setError(normalizeAuthMessage(j, t("EmailOtp.error.verificationFailed")));
        return;
      }
      setIsAuthed(true);
    } catch (err) {
      setError(
        normalizeAuthMessage(err?.message, t("EmailOtp.error.verificationFailed"))
      );
    } finally {
      setVerifying(false);
    }
  };

  if (isAuthed) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        {children}
      </>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-900">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="fixed top-4 right-4 z-20">
          <LanguageSwitcher className="min-w-[160px]" />
        </div>
        <div className="text-neutral-300">{t("EmailOtp.checking")}</div>
      </div>
    );
  }

  const notWhitelistedMsg = t("EmailOtp.error.notWhitelisted");
  const showNotWhitelisted =
    isWhitelisted === false ||
    (isWhitelisted === true && !isAllowedLevel(whitelistLevel));

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-neutral-900">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="fixed top-4 right-4 z-20">
        <LanguageSwitcher className="min-w-[160px]" />
      </div>

      <form
        onSubmit={handleVerify}
        className="bg-neutral-800 p-8 rounded-3xl shadow-lg w-80 flex flex-col items-stretch"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
        }}
      >
        <h2 className="text-2xl font-semibold mb-6 text-neutral-100 text-center">
          {t("EmailOtp.title")}
        </h2>

        <label className="text-sm text-neutral-300 mb-1">
          {t("EmailOtp.emailLabel")}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-3 mb-3 border border-neutral-700 rounded-xl bg-neutral-900 text-neutral-100 placeholder-neutral-500 text-base"
          placeholder={t("EmailOtp.emailPlaceholder")}
          autoComplete="email"
        />

        <div className="flex items-center justify-between mb-4 gap-4">
          <button
            type="button"
            onClick={handleRequestCode}
            disabled={!canSend}
            className={`px-3 py-2 rounded-xl font-semibold text-sm ${
canSend
? "bg-neutral-600 hover:bg-neutral-700 text-neutral-100"
: "bg-neutral-700/50 text-neutral-400 cursor-not-allowed"
}`}
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            {codeSent
              ? countdown > 0
                ? t("EmailOtp.resendWithCountdown", { s: countdown })
                : t("EmailOtp.resendCode")
              : t("EmailOtp.sendCode")}
          </button>
          <span className="text-xs text-neutral-400">
            {t("EmailOtp.codeValidityHint")}
          </span>
        </div>

        <label className="text-sm text-neutral-300 mb-1">
          {t("EmailOtp.codeLabel")}
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          className="w-full p-3 mb-4 border border-neutral-700 rounded-xl bg-neutral-900 text-neutral-100 placeholder-neutral-500 text-base tracking-widest"
          placeholder={t("EmailOtp.codePlaceholder")}
          autoComplete="one-time-code"
        />

        {/* 非白名单提示（在完成校验后且非白名单时展示） */}
        {showNotWhitelisted ? (
          <div className="text-red-500 text-xs mb-2 text-center font-medium">
            {notWhitelistedMsg}
          </div>
        ) : null}

        {error && error !== notWhitelistedMsg ? (
          <div className="text-red-500 text-sm mb-3 text-center font-medium">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="text-emerald-500 text-sm mb-3 text-center font-medium">
            {message}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canVerify}
          className={`w-full text-neutral-100 py-3 rounded-2xl font-semibold text-lg ${
canVerify
? "bg-neutral-600 hover:bg-neutral-700"
: "bg-neutral-700/50 cursor-not-allowed"
}`}
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          {t("EmailOtp.signIn")}
        </button>
      </form>
    </div>
  );
};

export default EmailOtpProtectedRoute;
